import React, {useState, useEffect, useMemo} from 'react'

import { useLocation, Link } from 'react-router-dom'
import { Button, LoadingFull, DataTable, Chip, SelectAsync, Person, Input } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import { useIntl, FormattedDate } from 'react-intl'
import SearchPage from 'containers/SearchPage'
import classNames from 'classnames'
import useApi from 'hooks/useApi'
import { Span, Timestamp, Contact, CompanyId, TeamAvatars } from 'components/datatable'
import debounce from 'debounce-promise'

const CustomersSuccessTeamsPage = ({ history, ...props }) => {
  const [customerList, loading, errors, refetch] = useApi(`/rest/v1/customer/success-teams`)
  const [coordinator, setCoordinator] = useState(null)
  const [query, setQuery] = useState('')
  const [sorting, setSorting] = useState({
    key: '',
    direction: 'DESC',
  })

  const customers = useMemo(() => {
    return customerList?.sort((a, b) => {
      if (typeof a[sorting.key] === 'string') {
        return sorting.direction === 'ASC'
          ? a[sorting.key].localeCompare(b[sorting.key])
          : b[sorting.key].localeCompare(a[sorting.key])
      }

      if (sorting.direction === 'DESC') {
        return parseInt(b[sorting.key]) - parseInt(a[sorting.key])
      }

      return parseInt(a[sorting.key]) - parseInt(b[sorting.key])
    })
      .filter((customer) => !!query.length
        ? customer.name.toLowerCase().includes(query.toLowerCase())
        : true
      )
      .filter((customer) => !coordinator ? true : (
          [
            customer.primaryCoordinator?.id,
            customer.secondaryCoordinator?.id,
            customer.primaryProducer?.id,
            customer.secondaryProducer?.id,
            customer.primarySales?.id,
            customer.secondarySales?.id,
          ].includes(coordinator?.value)
    )) || []
  }, [customerList, sorting, coordinator, query])

  return (
    <div className="grow overflow-y-scroll flex flex-col">
      <div className="shrink-0 p-2 grid grid-cols-3 gap-3">
        <div>
          <h1 className="text-xl font-bold">Success Teams</h1>
          <h2 className="text-sm text-grayscale-lighter">
            All classified customers are listed. Sales responsible and classification inherits from Bitrix24. Revenue is last fiscal year (1/7 - 21/6). Sort by clicking on the column header (name, country, revenue and classification).
          </h2>
        </div>

        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filer by customer name"
        />

        <SelectAsync
          value={coordinator}
          onChange={setCoordinator}
          cacheOptions
          loadOptions={
            debounce((query) => fetch(`/rest/v2/coordinator?pageSize=25&search[name]=${query}`)
              .then(response => response.json())
              .then((coordinators) => coordinators.map(({ uid, name }) => ({ value: +uid, label: name })))
              , 250)
          }
          openMenuOnFocus
          placeholder="Only show for user"
          isClearable
        />
      </div>

      <div className="grow overflow-scroll">
        { loading ? (
          <LoadingFull />
        ) : (
          <DataTable
            components={{}}
            head={{
              id: { sortable: false, className: 'w-16', label: 'ID' },
              company_id: { sortable: false, className: 'w-16', label: 'CRM' },
              name: { sortable: true, label: 'Name' },
              country: { sortable: true, label: 'Country' },
              classification: { sortable: true, label: 'Classification' },
              totalRevenueLastYear: { sortable: true, label: 'Revenue' },
              primarySales: { sortable: false, label: 'Sales' },
              primaryCoordinator: { sortable: false, label: 'PMs' },
              primaryProducer: { sortable: false, label: 'Producers' },
            }}
            body={{
              id: Span,
              company_id: CompanyId,
              name: ({ name, id }) => <Link className="hover:text-primary" to={`/customers/${id}`}>{ name }</Link>,
              country: Span,
              classification: Span,
              totalRevenueLastYear: ({ totalRevenueLastYear, currency }) => <Span>{ totalRevenueLastYear } { currency }</Span>,
              primarySales: ({ primarySales, secondarySales }) => (
                <TeamAvatars primary={primarySales} secondary={secondarySales} />
              ),
              primaryCoordinator: ({ primaryCoordinator, secondaryCoordinator }) => (
                <TeamAvatars primary={primaryCoordinator} secondary={secondaryCoordinator} />
              ),
              primaryProducer: ({ primaryProducer, secondaryProducer }) => (
                <TeamAvatars primary={primaryProducer} secondary={secondaryProducer} />
              ),
            }}
            entries={customers}
            indicators={[]}
            messages={{}}
            state={{}}
            sorting={sorting}
            setSorting={setSorting}
          />
        ) }
      </div>
    </div>
  )
}

export default CustomersSuccessTeamsPage
